'use client'

/**
 * Automatic Route Tracker (Issue #5115)
 *
 * Client component that listens to Next.js pathname changes
 * and fires automatic page view events for analytics.
 *
 * Mounted in the root layout to capture every authenticated
 * route transition, including SPA navigations.
 */

import { useEffect } from 'react'
import { usePathname } from 'next/navigation'
import { SHELL_ENDPOINTS, shellWrite } from '@/lib/shell-transport'
import { normalizeRoute, isExcludedRoute } from '@/lib/route-tracking'

/**
 * Hook that tracks page views on pathname changes.
 * Extracted as a hook for testability.
 */
export function useRouteTracking(): void {
  const pathname = usePathname()

  useEffect(() => {
    if (isExcludedRoute(pathname)) {
      return
    }

    const normalized = normalizeRoute(pathname)
    // Over HTTP, not as a server action (#13434). This effect fires on every
    // pathname change, so as a server action it put an entry in the App
    // Router's action queue at the exact moment the user was navigating —
    // and a queued action that outlives a navigation resolves the router to
    // the address the user LEFT. `src/lib/shell-transport.ts` has the
    // mechanism; the `startTransition` this replaces was about render
    // priority and never addressed it.
    //
    // Tracking is non-critical, so failures stay silent — `shellWrite` does
    // not throw.
    void shellWrite(SHELL_ENDPOINTS.pageView, {
      path: pathname,
      normalizedPath: normalized,
    })
  }, [pathname])
}

/**
 * Renders nothing — exists only for its side effect
 * of tracking route changes via useRouteTracking().
 */
export function RouteTracker() {
  useRouteTracking()
  return null
}
